import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Check, Zap, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import CheckoutModal from '@/components/CheckoutModal';
import WatchDemoButton from '@/components/tutorial/WatchDemoButton';
import HowItWorksSection from '@/components/tutorial/HowItWorksSection';
import {
  money,
  LEAD_PRICE,
  planBillingSuffix,
  planTermMonths,
  planTermNote,
  planFeatureLines,
  planLeads,
  BASIC_INTRO_OFFER,
  introOfferLine,
} from '@/data/constants';
import { normalizeCouponCode, rememberCoupon, readRememberedCoupon } from '@/lib/coupons';
import { authLink, clearPurchaseIntent, planReturnPath, savePurchaseIntent } from '@/lib/purchaseIntent';
import { canHoldSubscription } from '@/lib/roles';

import { BILLING_POLICY, BILLING_POLICY_HEADING, bi, biList } from '@/data/legal';

interface Plan {
  id: string;
  code: string;
  name_en: string;
  name_es: string;
  price: number;
  included_leads: number;
  features_en: string[];
  features_es: string[];
}

const Pricing: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile, refreshProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [active, setActive] = useState<Plan | null>(null);
  const [current, setCurrent] = useState<string>('');
  const [flash, setFlash] = useState('');
  const [pending, setPending] = useState(false);
  /** True once the membership is confirmed active — shows the next-step buttons. */
  const [activated, setActivated] = useState(false);
  const pollRef = useRef<number | null>(null);
  const leadRef = useRef<HTMLDivElement | null>(null);
  const deepLinked = useRef(false);
  /**
   * The status line lives at the TOP of the page, but on a phone the plan
   * buttons are far below it — so "You already have this plan" or "Memberships
   * are for contractors" used to appear off-screen and the tap looked dead.
   * Whenever a message appears, bring it into view.
   */
  const flashRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    if (!flash) return;
    window.setTimeout(() => flashRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 30);
  }, [flash]);

  /*
   * Plans come from the database. If that request failed (weak signal) the
   * grid used to render EMPTY with no message — a silent dead end right at
   * the buying decision. Now there is a loading line and a retry button.
   */
  const [plansState, setPlansState] = useState<'loading' | 'ok' | 'error'>('loading');
  const loadPlans = () => {
    setPlansState('loading');
    supabase
      .from('mkt_plans')
      .select('*')
      .eq('active', true)
      .order('position')
      .then(
        ({ data, error }) => {
          const rows = (data || []) as Plan[];
          setPlans(rows);
          setPlansState(error || rows.length === 0 ? 'error' : 'ok');
        },
        () => setPlansState('error'),
      );
  };
  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const readCurrentPlan = async () => {
    if (!profile) return '';
    const { data } = await supabase
      .from('mkt_subscriptions')
      .select('plan_code')
      .eq('user_id', profile.id)
      .eq('status', 'active')
      .limit(1);
    const code = data && data[0] ? data[0].plan_code : '';
    setCurrent(code);
    return code;
  };

  useEffect(() => {
    readCurrentPlan();
  }, [profile?.id]);

  useEffect(() => {
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  /** Shared promo links: /pricing?plan=pro&coupon=PRO10 — kept across the login round-trip. */
  const [couponFromLink] = useState(() => {
    const c = normalizeCouponCode(params.get('coupon') || '');
    if (c) rememberCoupon(c);
    return c || readRememberedCoupon();
  });

  const choose = (p: Plan) => {
    if (!profile) {
      /*
        NEW CUSTOMER PATH — straight to SIGN-UP (not sign-in), carrying this
        exact plan (+ promo code) in ?next= AND in localStorage, so after the
        account is created the plan's checkout opens by itself.
      */
      const returnTo = planReturnPath(path('/pricing'), p.code, couponFromLink || undefined);
      savePurchaseIntent({ kind: 'plan', plan: p.code, returnTo, label: lang === 'es' ? p.name_es : p.name_en });
      navigate(authLink(path('/register'), returnTo, 'plan'));
      return;
    }
    // Project-owner accounts cannot hold a membership (server enforces the
    // same rule). Say so plainly instead of opening a checkout that fails.
    if (!canHoldSubscription(profile.user_type)) {
      setPending(false);
      setFlash(
        lang === 'es'
          ? 'Las membresías son para contratistas. Su cuenta es de propietario de proyecto.'
          : 'Memberships are for contractors. Your account is a project-owner account.',
      );
      return;
    }
    // Already on this plan (e.g. a stale "continue" link): never open a second
    // checkout for the same membership.
    if (p.code === current) {
      setPending(false);
      // Paid already (e.g. activated in the other tab): the reminder is done.
      clearPurchaseIntent();
      setActivated(true);
      setFlash(lang === 'es' ? 'Ya tiene este plan activo.' : 'You already have this plan.');
      return;
    }
    setActive(p);
  };

  /**
   * DEEP LINK from the floating "Start Now" plan picker (PlansModal) and from
   * the sign-up return trip.
   *   ?plan=basic|pro|premium -> open that plan's checkout straight away
   *   ?plan=single|compare    -> scroll to the single-lead / plan comparison
   * Runs once per visit (deepLinked guard) and then strips the query param so
   * a refresh or a back-navigation does not reopen the modal unexpectedly.
   *
   * WAITS FOR AUTH. Previously this could run while the session was still
   * loading, see `profile === null`, and bounce an already signed-in member
   * to the sign-in page — the very loop new buyers described.
   */
  useEffect(() => {
    const wanted = params.get('plan');
    if (!wanted || deepLinked.current) return;

    if (wanted === 'single' || wanted === 'compare') {
      deepLinked.current = true;
      leadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      params.delete('plan');
      setParams(params, { replace: true });
      return;
    }

    // Plan codes need the plan rows AND the signed-in state to have loaded.
    if (!plans.length || authLoading) return;
    const match = plans.find((p) => p.code === wanted);
    deepLinked.current = true;
    params.delete('plan');
    params.delete('coupon');
    setParams(params, { replace: true });
    if (match) choose(match);
  }, [params, plans, profile?.id, authLoading]);



  /**
   * Called when the buyer has been sent to hosted checkout.
   * NOTHING is activated here — the plan only turns on when the platform
   * receives and validates the payment webhook. We simply poll the account so
   * the page flips to "active" the moment that happens.
   *
   * 2026-09-30 journey review:
   *  • The "you were joining a membership" reminder is now kept until the plan
   *    is ACTIVE (it used to be cleared the moment checkout opened, so a buyer
   *    who closed the payment tab without paying lost the one-tap way back).
   *  • On desktop the payment page opens in a NEW TAB. The buyer comes back to
   *    this tab, so we re-check the instant it regains focus instead of making
   *    them wait up to 5s — and we keep watching for 15 minutes, not ~3.
   */
  const stopWatching = useRef<() => void>(() => {});
  const watchForActivation = (plan: Plan) => {
    setActive(null);
    setPending(true);
    setFlash(t('pay.activationPending'));

    let ticks = 0;
    let done = false;
    const check = async () => {
      if (done) return;
      const code = await readCurrentPlan();
      if (code === plan.code && !done) {
        done = true;
        clearPurchaseIntent();
        await refreshProfile();
        setPending(false);
        setActivated(true);
        setFlash(t('pay.activated'));
        stopWatching.current();
      }
    };
    const onFocus = () => {
      if (document.visibilityState === 'visible') check();
    };
    stopWatching.current();
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(() => {
      ticks += 1;
      check();
      if (ticks > 180 && !done) {
        done = true;
        setPending(false);
        stopWatching.current();
        /* No silent stop: the "activating…" line used to stay on screen
           (turning green) after we stopped checking, which read as success. */
        setFlash(
          lang === 'es'
            ? 'Aún no vemos el pago. Si ya pagó, sus créditos aparecerán en Mi membresía en unos minutos; si no, elija el plan otra vez.'
            : 'We do not see the payment yet. If you paid, your credits will appear in My membership within a few minutes; if not, choose the plan again.',
        );
      }
    }, 5000);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    stopWatching.current = () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  };
  useEffect(() => () => stopWatching.current(), []);


  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      <Seo title={t('pricing.title')} description={t('pricing.sub')} />
      <div className="text-center">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t('pricing.title')}</h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-500">{t('pricing.sub')}</p>
        <div className="mt-5 flex justify-center">
          <WatchDemoButton
            placement="pricing-header"
            audience={profile?.user_type === 'gc' ? 'gc' : 'sub'}
            label={lang === 'es' ? '¿Cómo funciona? Ver demo de 2 min' : 'How does it work? Watch the 2-min demo'}
          />
        </div>
      </div>

      {flash && (
        /* Three tones: blue = still checking, green = plan is live, amber =
           something needs the buyer's attention. Warnings used to show green
           with a tick, which read as "done". */
        <p
          ref={flashRef}
          role="status"
          className={`mx-auto mt-6 flex max-w-xl items-center justify-center gap-2 rounded-xl px-4 py-3 text-center text-sm font-semibold ${
            pending ? 'bg-blue-50 text-blue-800' : activated ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'
          }`}
        >
          {pending || !activated ? <Clock className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />} {flash}
        </p>
      )}
      {/* ONE obvious next step once the membership is live — no hunting for
          where the leads are. */}
      {activated && (
        <div className="mx-auto mt-3 flex max-w-xl flex-col gap-2 sm:flex-row">
          <Link
            to={path('/jobs')}
            className="inline-flex min-h-[52px] flex-1 items-center justify-center rounded-xl bg-orange-500 px-5 py-3 text-base font-bold text-white hover:bg-orange-600"
          >
            {lang === 'es' ? 'Buscar leads ahora' : 'Find leads now'}
          </Link>
          <Link
            to={path('/dashboard')}
            className="inline-flex min-h-[52px] flex-1 items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-5 py-3 text-base font-bold text-slate-800 hover:bg-slate-50"
          >
            {lang === 'es' ? 'Ver mis créditos' : 'See my credits'}
          </Link>
        </div>
      )}


      <div
        ref={leadRef}
        className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:flex sm:items-center sm:justify-between"
      >

        <div>
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
            <Zap className="h-5 w-5 text-orange-500" /> {t('pricing.payPerLead')}
          </h2>
          <p className="mt-1 text-sm text-slate-500">{t('pricing.payPerLeadDesc')}</p>
        </div>
        <div className="mt-4 flex items-center justify-between gap-4 sm:mt-0 sm:justify-end">
          <div className="text-left sm:text-right">
            <p className="text-3xl font-extrabold text-slate-900">{money(LEAD_PRICE)}</p>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t('pricing.perLead')}</p>
          </div>
          {/* NO DEAD END: the $29 option used to be a price with no button, so
              a buyer who only wanted one lead had to guess where to go. */}
          <Link
            to={path('/jobs')}
            className="inline-flex min-h-[48px] shrink-0 items-center justify-center rounded-xl bg-orange-500 px-5 text-sm font-bold text-white hover:bg-orange-600"
          >
            {lang === 'es' ? 'Elegir un lead' : 'Pick a lead'}
          </Link>
        </div>
      </div>
      <p className="mt-3 text-center text-sm font-semibold text-slate-500">{t('pricing.fixedLeadNote')}</p>


      <h2 className="mt-12 text-2xl font-extrabold text-slate-900">{t('pricing.plans')}</h2>
      {plansState !== 'ok' && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-center">
          {plansState === 'loading' ? (
            <p className="text-sm font-semibold text-slate-500">{t('common.loading')}</p>
          ) : (
            <>
              <p className="text-sm font-semibold text-slate-700">
                {lang === 'es' ? 'No pudimos cargar los planes.' : 'We could not load the plans.'}
              </p>
              <button
                type="button"
                onClick={loadPlans}
                className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-xl bg-slate-900 px-5 text-sm font-bold text-white hover:bg-slate-800"
              >
                {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
              </button>
            </>
          )}
        </div>
      )}
      <div className="mt-6 grid gap-6 md:grid-cols-3">
        {plans.map((p, i) => {
          /**
           * NEVER render the stored bullets raw. planFeatureLines() rewrites
           * any "<N> included leads" bullet to the authoritative allowance in
           * PLAN_INCLUDED_LEADS, so a stale row (Premium said 20 long after
           * the grant moved to 25) can no longer advertise the wrong number.
           */
          const features = planFeatureLines(p.code, lang === 'es' ? p.features_es : p.features_en);
          const isCurrent = current === p.code;

          return (
            <div
              key={p.id}
              className={`flex flex-col rounded-2xl border p-6 ${
                i === 1 ? 'border-orange-300 bg-orange-50/40 shadow-lg ring-1 ring-orange-200' : 'border-slate-200 bg-white'
              }`}
            >
              <h3 className="text-lg font-extrabold text-slate-900">{lang === 'es' ? p.name_es : p.name_en}</h3>
              <p className="mt-2">
                <span className="text-3xl font-extrabold text-slate-900">{money(p.price)}</span>
                <span className="text-sm font-semibold text-slate-400">{planBillingSuffix(p.code, lang)}</span>
              </p>
              {/* The $50 intro month used to appear only INSIDE checkout, so the
                  card said $99 and the cheapest way in was invisible until after
                  the click. Shown up-front to anyone who is not a member yet. */}
              {p.code === BASIC_INTRO_OFFER.plan && !current && (
                <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold leading-relaxed text-emerald-900">
                  {introOfferLine(lang)}
                </p>
              )}
              {planTermMonths(p.code) > 1 && (
                <p className="mt-2 rounded-lg bg-slate-900/5 px-3 py-2 text-xs font-semibold leading-relaxed text-slate-700">
                  {planTermNote(p.code, p.price, lang)}
                </p>
              )}

              <ul className="mt-5 flex-1 space-y-2.5">
                {(features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(p)}
                disabled={isCurrent}
                className={`mt-6 w-full rounded-xl px-4 py-3.5 text-base font-bold ${
                  isCurrent ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {isCurrent ? t('pricing.currentPlan') : t('pricing.choosePlan')}
              </button>
            </div>
          );
        })}
      </div>

      <h2 className="mt-14 text-2xl font-extrabold text-slate-900">{t('pricing.howLeadsWork')}</h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        {['howLeads1', 'howLeads2', 'howLeads3'].map((k) => (
          <div key={k} className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-sm leading-relaxed text-slate-600">{t(`pricing.${k}`)}</p>
          </div>
        ))}
      </div>

      {/* Onboarding tutorial: explains credits, unlocking and where homeowner
          details appear, right where the buying decision is made. */}
      <div className="mt-14">
        <HowItWorksSection
          placement="pricing"
          tone="none"
          ctas={false}
          audience={profile?.user_type === 'gc' ? 'gc' : 'sub'}
          title={lang === 'es' ? 'Vea exactamente qué obtiene antes de comprar' : 'See exactly what you get before you buy'}
        />
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6">
        <h2 className="text-lg font-extrabold text-slate-900">{bi(BILLING_POLICY_HEADING, lang)}</h2>
        <ul className="mt-3 space-y-2">
          {biList(BILLING_POLICY, lang).map((p) => (
            <li key={p.slice(0, 24)} className="flex items-start gap-2 text-sm leading-relaxed text-slate-600">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {p}
            </li>
          ))}
        </ul>
        <Link to={path('/contact')} className="mt-4 inline-block text-sm font-bold text-orange-600 hover:underline">
          {t('pricing.contactSupport')}
        </Link>
      </div>

      <p className="mt-6 text-center text-sm font-semibold text-slate-500">{t('pricing.freeNote')}</p>


      {active && (
        <CheckoutModal
          open={!!active}
          onClose={() => setActive(null)}
          amount={active.price}
          title={t('pay.subscribeTitle')}
          subtitle={lang === 'es' ? active.name_es : active.name_en}
          lineItem={`${lang === 'es' ? active.name_es : active.name_en} — ${
            planLeads(active.code) || active.included_leads
          } ${t('dash.includedLeads')}`}

          planCode={active.code}
          kind="subscription"
          initialCoupon={couponFromLink}
          onCheckoutOpened={() => watchForActivation(active)}
        />
      )}


    </div>
  );
};

export default Pricing;
